import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminTitle, Modal, Field } from "@/pages/admin/AdminUI";
import { toast } from "sonner";

const empty = { question: "", answer: "", question_id: "", answer_id: "", category: "General", location: "faq", published: true, sort_order: 0 };
const LOCATIONS = ["faq", "homepage", "checkout"];

export default function AdminFaq() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/faq").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...empty, ...f }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const payload = { ...form, sort_order: +form.sort_order }; if (editing) await api.put(`/admin/faq/${editing.id}`, payload); else await api.post("/admin/faq", payload); toast.success("Saved"); setOpen(false); load(); }
    catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };
  const del = async (f) => { if (!window.confirm("Delete FAQ?")) return; await api.delete(`/admin/faq/${f.id}`); load(); };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  return (
    <div>
      <AdminTitle title="FAQ" action={<button onClick={openNew} className="gold-btn text-sm" data-testid="admin-add-faq"><Plus size={16} /> Add FAQ</button>} />
      <div className="space-y-3">
        {items.map((f) => (
          <div key={f.id} className="card-lux p-5 flex items-start justify-between gap-4" data-testid={`admin-faq-${f.id}`}>
            <div>
              <div className="text-white font-medium">{f.question}</div>
              <div className="text-sm text-slate-400 mt-1">{f.answer}</div>
              <div className="text-xs text-slate-600 mt-2">{f.category} · {f.location} · {f.published ? "Published" : "Hidden"}</div>
            </div>
            <div className="flex gap-3 shrink-0">
              <button onClick={() => openEdit(f)} className="text-slate-400 hover:text-amber-400" data-testid={`edit-faq-${f.id}`}><Pencil size={15} /></button>
              <button onClick={() => del(f)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit FAQ" : "New FAQ"}>
        <form onSubmit={save} className="space-y-4" data-testid="faq-form">
          <Field label="Question (EN)"><input required className="input-lux" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question-field" /></Field>
          <Field label="Answer (EN)"><textarea required rows={3} className="input-lux resize-none" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer-field" /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Category"><input className="input-lux" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
            <Field label="Location"><select className="input-lux" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}>{LOCATIONS.map((l) => <option key={l}>{l}</option>)}</select></Field>
            <Field label="Sort Order"><input type="number" className="input-lux" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-amber-500" /> Published</label>
          <button disabled={saving} className="gold-btn w-full" data-testid="faq-save-btn">{saving ? <Loader2 className="animate-spin" size={18} /> : "Save"}</button>
        </form>
      </Modal>
    </div>
  );
}
